import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  viewPreferencesApi,
  ViewPreferenceQuery,
  ViewPreferencePayload,
} from '@/lib/api';

export function useViewPreference(query: ViewPreferenceQuery | null) {
  return useQuery({
    queryKey: ['viewPreference', query?.scopeType, query?.scopeId, query?.viewType],
    queryFn: async () => {
      if (!query) return null;
      const response = await viewPreferencesApi.get(query);
      return response.data.preference;
    },
    enabled: !!query?.scopeId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

export function useSetViewPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ViewPreferencePayload) => {
      const response = await viewPreferencesApi.upsert(payload);
      return response.data;
    },
    onMutate: async (payload) => {
      // Optimistic update
      const queryKey = [
        'viewPreference',
        payload.scopeType,
        payload.scopeId,
        payload.viewType,
      ];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey) as
        | { preferences: Record<string, unknown> }
        | null
        | undefined;
      queryClient.setQueryData(queryKey, (current: { preferences?: Record<string, unknown> } | null) => {
        const existingPreferences = current?.preferences ?? {};
        return {
          ...(current ?? {
            scopeType: payload.scopeType,
            scopeId: payload.scopeId,
            viewType: payload.viewType,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }),
          preferences: {
            ...existingPreferences,
            ...payload.preferences,
          },
        };
      });
      return { previous, queryKey };
    },
    onError: (_err, _payload, context) => {
      // Revert on error
      if (context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previous ?? null);
      }
    },
    // No onSettled invalidation: the PUT endpoint returns only `{ success: boolean }`
    // (no preference payload), so a post-mutation GET refetch would discard the
    // optimistic merge to re-fetch identical data. The optimistic update in
    // onMutate is canonical; onError reverts. See A1.6 (Phase A roadmap).
  });
}

export function useClearViewPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (query: ViewPreferenceQuery) => {
      await viewPreferencesApi.clear(query);
    },
    onSuccess: (_, query) => {
      // Authoritative local state after a successful clear is `null`; no GET refetch needed.
      queryClient.setQueryData(
        ['viewPreference', query.scopeType, query.scopeId, query.viewType],
        null,
      );
    },
  });
}
